import { styled, alpha } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table as MuiTable,
  TableHead as MuiTableHead,
  TableBody as MuiTableBody,
  TableRow as MuiTableRow,
  TableCell,
  Modal,
  IconButton,
} from '@mui/material';
import { colors, spacing } from '../../../styles/theme';
import { getTableRowHoverOverlay } from '../../../styles/theme/tableRowOverlays';

// Main container
export const SystemParametersContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2), // Mantener gap reducido
  height: '100%',
  backgroundColor: 'transparent',
  paddingTop: 0, // Eliminar padding-top para acercar los Tabs a las pestañas
}));

// Categories Cards Container - Diseño elegante y moderno
export const TabsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 160px))',
  gap: '8px',
  marginBottom: 0,
  paddingTop: '6px', // Espacio para la animación translateY(-3px) + borde de 3px
  justifyContent: 'flex-start',
  paddingLeft: 0,
  paddingRight: 0,
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
    gap: '6px',
  },
}));

// Card individual para cada categoría - Diseño elegante con glassmorphism
export const Tab = styled(Box)<{ active?: boolean; categoryColor?: string }>(({
  theme,
  active,
  categoryColor,
}) => {
  const accent = categoryColor || '#10b981';
  const isDark = theme.palette.mode === 'dark';
  const inactiveBg = isDark ? alpha(theme.palette.common.white, 0.06) : '#ffffff';
  const inactiveBorder = isDark ? theme.palette.divider : '#e2e8f0';
  const activeBg = isDark ? alpha(accent, 0.14) : alpha(accent, 0.08);
  const color = accent;
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10px 14px',
    cursor: 'pointer',
    width: '100%',
    minWidth: '160px',
    backgroundColor: active ? activeBg : inactiveBg,
    border: active ? `2px solid ${color}` : `2px solid ${inactiveBorder}`,
    borderTop: active ? `2px solid ${color}` : `2px solid ${inactiveBorder}`,
    borderRadius: '10px',
    position: 'relative',
    overflow: 'visible', // Cambiar de 'hidden' a 'visible' para que el borde no se corte durante la animación
    backdropFilter: 'blur(10px)',
    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
    animation: 'cardFadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) backwards',
    boxShadow: active
      ? isDark
        ? `0 10px 40px -10px ${alpha(accent, 0.35)}, 0 1px 2px rgba(0, 0, 0, 0.2)`
        : `0 10px 40px -10px ${alpha(accent, 0.2)}, 0 1px 2px rgba(0, 0, 0, 0.05)`
      : isDark
        ? '0 1px 3px rgba(0, 0, 0, 0.25)'
        : '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)',
    '&:nth-of-type(1)': {
      animationDelay: '0.05s',
    },
    '&:nth-of-type(2)': {
      animationDelay: '0.1s',
    },
    '&:nth-of-type(3)': {
      animationDelay: '0.15s',
    },
    '&:nth-of-type(4)': {
      animationDelay: '0.2s',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '-50%',
      left: '-50%',
      width: '200%',
      height: '200%',
      background: active
        ? `radial-gradient(circle, ${alpha(accent, isDark ? 0.14 : 0.1)} 0%, transparent 70%)`
        : 'transparent',
      opacity: active ? 1 : 0,
      transition: 'opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
      pointerEvents: 'none',
    },
    '&:hover': {
      transform: 'translateY(-3px) scale(1.01)',
      boxShadow: active
        ? isDark
          ? `0 20px 60px -15px ${alpha(accent, 0.45)}, 0 4px 6px rgba(0, 0, 0, 0.2)`
          : `0 20px 60px -15px ${alpha(accent, 0.25)}, 0 4px 6px rgba(0, 0, 0, 0.07)`
        : isDark
          ? '0 10px 25px -5px rgba(0, 0, 0, 0.35)'
          : '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
      backgroundColor: active
        ? isDark
          ? alpha(accent, 0.18)
          : alpha(accent, 0.12)
        : isDark
          ? alpha(theme.palette.common.white, 0.1)
          : '#fafbfc',
      border: active
        ? `2px solid ${color}`
        : `2px solid ${isDark ? theme.palette.divider : '#cbd5e1'}`,
      borderTop: active
        ? `2px solid ${color}`
        : `2px solid ${isDark ? theme.palette.divider : '#cbd5e1'}`,
      '&::after': {
        opacity: 0.5,
      },
    },
    '&:active': {
      transform: 'translateY(-2px) scale(1.01)',
      transition: 'all 0.15s cubic-bezier(0.16, 1, 0.3, 1)',
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-visible': {
      outline: 'none',
    },
  };
});

// Icono de la categoría - Diseño elegante con animación suave
export const TabIcon = styled(Box)<{ active?: boolean; categoryColor?: string }>(({
  theme,
  active,
  categoryColor,
}) => {
  const accent = categoryColor || '#10b981';
  const isDark = theme.palette.mode === 'dark';
  const inactiveIconBg = isDark ? alpha(theme.palette.common.white, 0.08) : '#f1f5f9';
  return {
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '8px',
    alignSelf: 'center',
    position: 'relative',
    backgroundColor: active ? alpha(accent, isDark ? 0.22 : 0.15) : inactiveIconBg,
    color: active ? accent : theme.palette.text.secondary,
    transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
    boxShadow: active
      ? `0 4px 12px ${alpha(accent, isDark ? 0.35 : 0.15)}`
      : isDark
        ? 'none'
        : '0 2px 4px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
    '& svg': {
      width: '14px',
      height: '14px',
      transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
      filter: active ? `drop-shadow(0 2px 4px ${alpha(accent, 0.25)})` : 'none',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: '8px',
      padding: '2px',
      background: active
        ? `linear-gradient(135deg, ${alpha(accent, 0.22)}, ${alpha(accent, 0.08)})`
        : 'transparent',
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      opacity: active ? 1 : 0,
      transition: 'opacity 0.4s ease',
    },
    [`${Tab}:hover &`]: {
      backgroundColor: active
        ? alpha(accent, isDark ? 0.28 : 0.2)
        : isDark
          ? alpha(theme.palette.common.white, 0.12)
          : '#e2e8f0',
      transform: 'scale(1.06) rotate(2deg)',
      boxShadow: active
        ? `0 8px 20px ${alpha(accent, isDark ? 0.4 : 0.2)}`
        : isDark
          ? '0 4px 8px rgba(0, 0, 0, 0.2)'
          : '0 4px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
      '& svg': {
        transform: 'scale(1.12)',
        filter: active
          ? `drop-shadow(0 4px 8px ${alpha(accent, 0.5)})`
          : 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
      },
    },
  };
});

// Contenedor para label y contador en la misma línea
export const TabLabelContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  width: '100%',
  flexWrap: 'nowrap',
}));

// Label de la categoría - Tipografía elegante
export const TabLabel = styled(Typography)<{ active?: boolean; categoryColor?: string }>(({
  theme,
  active,
  categoryColor,
}) => {
  const accent = categoryColor || '#10b981';
  return {
    fontSize: '13px',
    fontWeight: active ? 700 : 600,
    color: active ? accent : theme.palette.text.primary,
    marginBottom: 0,
    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
    letterSpacing: '-0.02em',
    lineHeight: 1.3,
    position: 'relative',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    [`${Tab}:hover &`]: {
      color: active ? accent : theme.palette.text.primary,
      transform: 'none',
    },
  };
});

// Contador de elementos - Badge elegante
export const TabCount = styled(Box)<{ active?: boolean; categoryColor?: string }>(({
  theme,
  active,
  categoryColor,
}) => {
  const accent = categoryColor || '#10b981';
  const isDark = theme.palette.mode === 'dark';
  const inactiveBg = isDark ? alpha(theme.palette.common.white, 0.08) : '#f1f5f9';
  return {
    fontSize: '11px',
    fontWeight: 700,
    color: active ? accent : theme.palette.text.secondary,
    backgroundColor: active ? alpha(accent, isDark ? 0.2 : 0.12) : inactiveBg,
    padding: '2px 6px',
    borderRadius: '4px',
    minWidth: '20px',
    textAlign: 'center',
    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
    border: active ? `1px solid ${alpha(accent, 0.35)}` : `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: active ? `0 2px 4px ${alpha(accent, isDark ? 0.25 : 0.12)}` : 'none',
    letterSpacing: '0.02em',
    [`${Tab}:hover &`]: {
      backgroundColor: active
        ? alpha(accent, isDark ? 0.28 : 0.18)
        : isDark
          ? alpha(theme.palette.common.white, 0.12)
          : '#e2e8f0',
      transform: 'scale(1.06)',
      boxShadow: active ? `0 4px 8px ${alpha(accent, 0.3)}` : 'none',
      borderColor: active ? alpha(accent, 0.45) : theme.palette.divider,
    },
  };
});

// Main content
export const MainContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: 0,
  paddingTop: 0, // Sin padding-top para acercar los Tabs
  overflow: 'auto',
  backgroundColor: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

export const ContentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  marginBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
}));

export const ContentTitle = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  marginBottom: '4px',
}));

export const ContentSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.secondary,
}));

export const ContentActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

// Add Button - Degradado verde del sistema
export const AddButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  background: 'linear-gradient(to right, #06b6d4, #10b981)',
  color: colors.background.paper,
  padding: '10px 16px',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  boxShadow: '0 2px 4px rgba(6, 182, 212, 0.2)',
  '&:hover': {
    background: 'linear-gradient(to right, #0891b2, #059669)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)',
  },
}));

// Search section
export const SearchContainer = styled(Box)(({ theme }) => ({
  marginBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
}));

export const SearchField = styled(TextField)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  const bgDefault = isDark ? alpha(theme.palette.common.white, 0.08) : '#ffffff';
  const bgHover = isDark ? alpha(theme.palette.common.white, 0.1) : '#ffffff';
  const bgFocus = isDark ? alpha(theme.palette.common.white, 0.12) : '#ffffff';
  return {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: bgDefault,
      borderRadius: '8px',
      '&:hover': {
        backgroundColor: bgHover,
        '& fieldset': {
          borderColor: theme.palette.success.main,
          borderWidth: '2px',
        },
      },
      '&.Mui-focused': {
        backgroundColor: bgFocus,
        '& fieldset': {
          borderColor: '#0BB296',
          borderWidth: '2px',
          boxShadow: isDark
            ? '0 0 0 3px rgba(12, 187, 154, 0.2)'
            : '0 0 0 3px rgba(12, 187, 154, 0.12)',
        },
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      '& legend': {
        maxWidth: 'none',
        width: 'fit-content',
        minWidth: 0,
        padding: '0 8px',
        boxSizing: 'content-box',
      },
    },
  };
});

// Table styles — alineados con `common/Table/Table.styles` (tablas estándar del sistema)
export const TableContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(2),
  border: 'none',
  overflow: 'hidden',
  boxShadow: 'none',
}));

export const Table = styled(MuiTable)(({ theme }) => ({
  minWidth: '100%',
  backgroundColor: theme.palette.background.paper,
}));

export const TableHead = styled(MuiTableHead)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(15, 23, 42, 0.03)',
  position: 'relative',
  overflow: 'hidden',
  '& .MuiTableRow-root': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiTableCell-root': {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: '0.9rem',
    letterSpacing: 0,
    textTransform: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: 'none',
    borderLeft: 'none',
    background: 'transparent',
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    lineHeight: 1.4,
  },
}));

export const TableRow = styled(MuiTableRow)(({ theme }) => {
  const hoverBg = getTableRowHoverOverlay(theme);
  const hoverImportant = `${hoverBg} !important`;
  const baseBg = theme.palette.background.paper;
  const rowBorder =
    theme.palette.mode === 'dark'
      ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
      : `1px solid ${colors.grey[50]}`;
  return {
    transition: 'background 0.2s ease-in-out',
    backgroundColor: baseBg,
    borderBottom: rowBorder,
    borderLeft: 'none',
    borderRight: 'none',
    '.MuiTableHead-root &': {
      borderBottom: 'none',
    },
    '& .MuiTableCell-root': {
      background: `${baseBg} !important`,
      backgroundColor: `${baseBg} !important`,
    },
    '&:hover .MuiTableCell-root': {
      background: hoverImportant,
      backgroundColor: hoverImportant,
    },
  };
});

export const TableHeaderCell = styled(TableCell)(({ theme }) => ({
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'none',
  letterSpacing: 0,
  lineHeight: 1.4,
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: 'none',
  borderLeft: 'none',
  whiteSpace: 'nowrap',
  backgroundColor: theme.palette.background.paper,
  '& .MuiTableSortLabel-root': {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
  },
}));

export const TableBody = styled(MuiTableBody)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
}));

export const TableDataCell = styled(TableCell)(({ theme }) => ({
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: 1.5,
  color: theme.palette.text.primary,
  borderBottom: 'none',
  borderLeft: 'none',
  borderRight: 'none',
  background: 'transparent',
  backgroundColor: 'transparent',
}));

export const ColorDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

export const ColorCircle = styled(Box)<{ color: string }>(({ theme, color }) => ({
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  border: '1px solid #d1d5db',
  backgroundColor: color,
}));

// Action buttons
export const ActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
}));

export const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: '8px',
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.hover : colors.grey[100],
  },
}));

export const EditButton = styled(ActionButton)(({ theme }) => ({
  color: colors.primary.main,
  '&:hover': {
    backgroundColor: alpha(colors.primary.main, 0.12),
    color: colors.primary.main,
  },
}));

export const DeleteButton = styled(ActionButton)(({ theme }) => ({
  color: colors.primary.main,
  '&:hover': {
    backgroundColor: alpha(colors.primary.main, 0.12),
    color: colors.primary.main,
  },
}));

// Empty state
export const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '48px 24px',
  textAlign: 'center',
}));

export const EmptyStateIcon = styled('svg')(({ theme }) => ({
  width: '48px',
  height: '48px',
  color: '#9ca3af',
  marginBottom: '16px',
}));

export const EmptyStateText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '14px',
}));

// Modal styles
export const ModalOverlay = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: spacing.md,
}));

export const ModalContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 24px 48px rgba(0, 0, 0, 0.55)'
      : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
  maxWidth: '512px',
  width: '100%',
  maxHeight: '90vh',
  overflow: 'auto',
  backgroundImage: 'none',
}));

export const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '24px 24px 0 24px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  paddingBottom: '24px',
}));

export const ModalTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

export const ModalCloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
  },
}));

export const ModalContent = styled(Box)(({ theme }) => ({
  padding: '24px',
}));

export const FormField = styled(Box)(({ theme }) => ({
  marginBottom: '16px',
}));

export const FormLabel = styled(Typography)(({ theme }) => ({
  display: 'block',
  fontSize: '14px',
  fontWeight: 500,
  color: theme.palette.text.primary,
  marginBottom: spacing.xs,
  textTransform: 'capitalize',
}));

export const FormInput = styled(TextField)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  const inputBg = isDark ? alpha(theme.palette.common.white, 0.08) : theme.palette.background.paper;
  return {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: inputBg,
      '&:hover': {
        backgroundColor: isDark ? alpha(theme.palette.common.white, 0.1) : inputBg,
        '& fieldset': {
          borderColor: theme.palette.success.main,
        },
      },
      '&.Mui-focused': {
        backgroundColor: isDark ? alpha(theme.palette.common.white, 0.12) : inputBg,
        '& fieldset': {
          borderColor: '#0BB296',
        },
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
    },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      '& legend': {
        maxWidth: 'none',
        width: 'fit-content',
        minWidth: 0,
        padding: '0 8px',
        boxSizing: 'content-box',
      },
    },
  };
});

export const ColorInput = styled('input')(({ theme }) => ({
  width: '100%',
  height: '40px',
  border: `1px solid ${alpha(theme.palette.success.main, 0.45)}`,
  borderRadius: '8px',
  cursor: 'pointer',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#fff',
  '&:hover': {
    borderColor: theme.palette.success.main,
  },
  '&:focus': {
    outline: 'none',
    borderColor: '#0BB296',
    borderWidth: '2px',
  },
}));

export const TextAreaInput = styled(TextField)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  const inputBg = isDark ? alpha(theme.palette.common.white, 0.08) : theme.palette.background.paper;
  return {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: inputBg,
      '&:hover': {
        backgroundColor: isDark ? alpha(theme.palette.common.white, 0.1) : inputBg,
        '& fieldset': {
          borderColor: theme.palette.success.main,
        },
      },
      '&.Mui-focused': {
        backgroundColor: isDark ? alpha(theme.palette.common.white, 0.12) : inputBg,
        '& fieldset': {
          borderColor: '#0BB296',
        },
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
    },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      '& legend': {
        maxWidth: 'none',
        width: 'fit-content',
        minWidth: 0,
        padding: '0 8px',
        boxSizing: 'content-box',
      },
    },
  };
});

export const ModalActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '24px',
}));

export const CancelButton = styled(Button)(({ theme }) => ({
  padding: '10px 16px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  color: theme.palette.text.secondary,
  textTransform: 'none',
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
    boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
}));

export const SaveButton = styled(Button)(({ theme }) => ({
  padding: '10px 16px',
  background: 'linear-gradient(to right, #06b6d4, #10b981)',
  color: colors.background.paper,
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 2px 4px rgba(6, 182, 212, 0.2)',
  '&:hover': {
    background: 'linear-gradient(to right, #0891b2, #059669)',
    boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)',
  },
  '&:disabled': {
    background: theme.palette.mode === 'dark' ? theme.palette.action.disabledBackground : '#9ca3af',
    color: theme.palette.mode === 'dark' ? theme.palette.action.disabled : '#ffffff',
    opacity: theme.palette.mode === 'dark' ? 1 : 0.6,
    cursor: 'not-allowed',
    boxShadow: 'none',
    '&:hover': {
      background:
        theme.palette.mode === 'dark' ? theme.palette.action.disabledBackground : '#9ca3af',
      boxShadow: 'none',
    },
  },
}));

// Animaciones elegantes
const elegantAnimations = `
  @keyframes cardFadeIn {
    0% {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }

  @keyframes iconFloat {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-3px);
    }
  }

  @keyframes pulseGlow {
    0%, 100% {
      box-shadow: 0 10px 40px -10px rgba(139, 92, 246, 0.3);
    }
    50% {
      box-shadow: 0 15px 50px -10px rgba(139, 92, 246, 0.4);
    }
  }
`;

// Inyectar las animaciones en el DOM
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = elegantAnimations;
  if (!document.head.querySelector('style[data-elegant-animations]')) {
    style.setAttribute('data-elegant-animations', 'true');
    document.head.appendChild(style);
  }
}

// Card Grid Layout - Para categorías simples
export const CardsGridContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 0),
  paddingLeft: 0,
  paddingRight: 0,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(1.5),
  },
}));

// Card individual
export const ItemCard = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.06)
      : theme.palette.background.paper,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 12px 24px rgba(0, 0, 0, 0.35)'
        : '0 12px 24px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(0, 0, 0, 0.06)',
    borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : '#cbd5e1',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(to right, #06b6d4, #10b981)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover::before': {
    opacity: 1,
  },
}));

// Card Header con título principal
export const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1.5),
}));

export const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: 1,
}));

// Card Actions (botones de editar/eliminar)
export const CardActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  opacity: 1,
  transition: 'opacity 0.2s ease',
  [`${ItemCard}:hover &`]: {
    opacity: 1,
  },
}));

export const CardActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.75),
  color: colors.primary.main,
  '&:hover': {
    backgroundColor: alpha(colors.primary.main, 0.12),
    color: colors.primary.main,
  },
}));

// Card Content
export const CardContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

export const CardField = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

export const CardFieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}));

export const CardFieldValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.95rem',
  color: theme.palette.text.primary,
  lineHeight: 1.5,
  wordBreak: 'break-word',
}));

// Color Badge para etiquetas
export const ColorBadge = styled(Box)<{ color: string }>(({ theme, color }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
  borderRadius: '6px',
  backgroundColor: alpha(color || '#6b7280', 0.1),
  border: `1px solid ${alpha(color || '#6b7280', 0.2)}`,
}));

export const ColorDot = styled(Box)<{ color: string }>(({ theme, color }) => ({
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  backgroundColor: color || '#6b7280',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  flexShrink: 0,
}));

// Empty state para cards
export const CardsEmptyState = styled(Box)(({ theme }) => ({
  gridColumn: '1 / -1',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',
}));
